import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { useMemo, useState } from 'react'
import { Button } from 'stories/components'

import { IMcrAdminView, IMcrConfig } from './types'

/**
 * A general ledger account pinned to an exact NMLS upload field.
 *
 * The balance sheet maps itself, because a Schedule A or B line means one thing. Schedules C and D
 * do not: income and expense are reported across business-line columns — residential production,
 * servicing, portfolio management, multifamily/commercial — and which column a company's interest
 * expense belongs to is an allocation the licensee makes, not something a chart of accounts
 * discloses. Rather than guess, the report leaves those lines out and says so; this is where the
 * decision gets recorded once so it holds every quarter.
 *
 * Field names are validated against the schema's own field list, so a typo cannot reach NMLS.
 */
export const McrXmlFieldMap = ({
  view,
  config,
  onChange,
}: {
  view: IMcrAdminView
  config: IMcrConfig
  onChange: (next: IMcrConfig) => void
}) => {
  const [newField, setNewField] = useState('')
  const [newAccount, setNewAccount] = useState('')

  const fieldsByName = useMemo(
    () => Object.fromEntries(view.xmlFields.map((f) => [f.name, f])),
    [view.xmlFields],
  )
  const entries = Object.entries(config.xmlFieldMap || {})
  const candidate = fieldsByName[newField.trim().toUpperCase()]

  const add = () => {
    if (!candidate || !newAccount) return
    const next = cloneDeep(config)
    next.xmlFieldMap[candidate.name] = newAccount
    onChange(next)
    setNewField('')
    setNewAccount('')
  }

  const remove = (field: string) => {
    const next = cloneDeep(config)
    delete next.xmlFieldMap[field]
    onChange(next)
  }

  const setAccount = (field: string, accountNumber: string) => {
    const next = cloneDeep(config)
    if (!accountNumber) delete next.xmlFieldMap[field]
    else next.xmlFieldMap[field] = accountNumber
    onChange(next)
  }

  const accountLabel = (account: { number: string; name: string }) =>
    `${account.number ? `${account.number} - ` : ''}${account.name}`

  return (
    <div>
      <p className="mb-3 text-[12px] text-gray-500">
        Optional. The Financial Condition balance sheet maps itself from the chart of accounts; this is
        for the Schedule C and D income and expense lines, which NMLS reports by business line and only
        you can allocate. A field named here overrides whatever the report would otherwise have put in
        it. {view.xmlFields.length} Financial Condition fields are available — start typing a code such
        as C500_1 or a phrase from its name.
      </p>

      {!view.accounts.length && (
        <p className="mb-3 text-sm text-amber-700">
          No general ledger accounts exist yet, so there is nothing to map from.
        </p>
      )}

      <table className="w-full text-sm">
        <thead className="uppercase text-xs bg-gray-50">
          <tr>
            {['NMLS Field', 'Line', 'General Ledger Account', ''].map((v, i) => (
              <th scope="col" className="p-3 border" key={`header-${v}-${i}`}>
                {v}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map(([field, accountNumber]) => {
            const meta = fieldsByName[field]
            return (
              <tr key={field}>
                <td className="px-2 border font-mono text-[12px]">{field}</td>
                <td className="px-2 border text-[12px] text-gray-500">
                  {meta ? `${meta.label || meta.section}` : 'Not a field in the FV6 schema'}
                </td>
                <td className="px-2 py-1 border w-80">
                  <select
                    className="block rounded py-1.5 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gray-200"
                    value={accountNumber}
                    onChange={(e) => setAccount(field, e.target.value)}
                  >
                    <option value="">- Select -</option>
                    {view.accounts.map((account) => (
                      <option key={account.no} value={account.number}>
                        {accountLabel(account)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 border w-12 text-center">
                  <button type="button" onClick={() => remove(field)} title="Remove mapping">
                    <TrashIcon className="mx-auto h-4 w-4 text-gray-400 hover:text-rose-600" />
                  </button>
                </td>
              </tr>
            )
          })}
          {!entries.length && (
            <tr>
              <td className="p-3 border text-[12px] text-gray-400" colSpan={4}>
                No fields pinned. Schedules C and D are left blank in the upload and flagged on the
                report for you to complete in NMLS.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <p className="mb-1 text-[12px] text-gray-600">NMLS Field</p>
          <input
            type="text"
            list="mcr-xml-fields"
            className="w-56 rounded border border-gray-300 px-2 py-1.5 font-mono text-sm"
            placeholder="C500_1"
            value={newField}
            onChange={(e) => setNewField(e.target.value)}
          />
          <datalist id="mcr-xml-fields">
            {view.xmlFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label || field.section}
              </option>
            ))}
          </datalist>
          <p className="mt-1 h-4 text-[11px] text-gray-400">
            {newField.trim() && !candidate ? 'Not a field in the FV6 schema.' : candidate?.label || ''}
          </p>
        </div>
        <div>
          <p className="mb-1 text-[12px] text-gray-600">General Ledger Account</p>
          <select
            className="w-80 rounded border border-gray-300 px-2 py-1.5 text-sm"
            value={newAccount}
            onChange={(e) => setNewAccount(e.target.value)}
          >
            <option value="">- Select -</option>
            {view.accounts.map((account) => (
              <option key={account.no} value={account.number}>
                {accountLabel(account)}
              </option>
            ))}
          </select>
          <p className="mt-1 h-4" />
        </div>
        <Button color="gray" disabled={!candidate || !newAccount} onClick={add}>
          Add Mapping
        </Button>
      </div>
    </div>
  )
}
